import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { mq } from '../utils/styledComponentsUtils';
import { isMobile } from 'react-device-detect';
import { Text, Box, HStack, Icon, useTheme } from '@wegroup/design-system';
import { formatAddress } from '../utils/commonUtils';
import { Address } from '../../../../types/Common';

interface CustomOptionProps {
  isLegal?: boolean;
  label_email?: string;
  label_phone?: string;
  label_address?: Address;
  label_external_refs?: Array<{ company?: string; ref?: string }>;
  label: string;
  value: string | number;
  isSelected: boolean;
}

const SearchSelectCustomOption: React.FC<
  React.PropsWithChildren<CustomOptionProps>
> = ({
  isLegal,
  label,
  label_phone,
  label_email,
  label_address,
  label_external_refs,
  isSelected,
}) => {
  const { colors } = useTheme();
  const ref: React.RefObject<HTMLDivElement> = useRef(null);

  useEffect(() => {
    if (isMobile && isSelected && ref.current?.scrollIntoView)
      ref.current?.scrollIntoView();
  }, [isSelected]);

  return (
    <div
      onClick={(e) => e.preventDefault()}
      className="custom-option_container"
      ref={ref}
      data-test-id="SEARCH-SELECT-INPUT_OPTION"
    >
      <StyledOption isMobileDevice={isMobile}>
        {!isSelected ? (
          <HStack justifyContent="space-between" w="100%" p="1">
            <Box>
              <HStack spacing="2">
                {isLegal ? (
                  <Icon
                    name="RiskObjectsPropertyBaggageLuggage"
                    boxSize={4}
                    color="black"
                  />
                ) : (
                  <Icon name="GeneralOthersAvatar" boxSize={4} color="black" />
                )}
                <Text textStyle="normalBodyBold" color="gray.700" mb="1">
                  {label}
                </Text>
              </HStack>
              <HStack spacing="4">
                {label_email && (
                  <HStack spacing="1" alignItems="center">
                    <Icon
                      name="IconActionMessage"
                      boxSize="4"
                      color={colors.gray[400]}
                    />
                    <Text
                      textStyle="smallBody"
                      lineHeight="150%"
                      color="gray.400"
                    >
                      {label_email}
                    </Text>
                  </HStack>
                )}
                {label_phone && (
                  <HStack spacing="1">
                    <Icon
                      name="IconActionPhone"
                      boxSize="4"
                      color={colors.gray[400]}
                    />
                    <Text
                      textStyle="smallBody"
                      lineHeight="150%"
                      color="gray.400"
                    >
                      {label_phone}
                    </Text>
                  </HStack>
                )}
              </HStack>
              {label_address && (
                <HStack spacing="1">
                  <Icon
                    name="IconPropertyFilled"
                    boxSize="4"
                    color={colors.gray[400]}
                  />
                  <Text
                    textStyle="smallBody"
                    lineHeight="150%"
                    color="gray.400"
                  >
                    {formatAddress(label_address)}
                  </Text>
                </HStack>
              )}
              {label_external_refs && label_external_refs.length > 0 && (
                <HStack spacing="4">
                  {label_external_refs.map((er, idx) => (
                    <HStack key={idx} spacing="1" alignItems="center">
                      <Icon
                        name="GeneralActionsLink"
                        boxSize="4"
                        color={colors.gray[400]}
                      />
                      <Text
                        textStyle="smallBody"
                        lineHeight="150%"
                        color="gray.400"
                      >
                        {`${er.company?.toUpperCase() || 'External'}: ${
                          er.ref || ''
                        }`}
                      </Text>
                    </HStack>
                  ))}
                </HStack>
              )}
            </Box>
            <Icon name="IconArrow" boxSize="5" color={colors.gray[700]} />
          </HStack>
        ) : (
          label
        )}
        <MobileRadioIcon
          isMobileDevice={isMobile}
          className="custom-option_radioIcon"
        >
          <div className="custom-option_radioIcon_fill" />
        </MobileRadioIcon>
      </StyledOption>
    </div>
  );
};

const MobileRadioIcon = styled.div<{ isMobileDevice?: boolean }>`
  width: 1.6rem;
  height: 1.6rem;
  position: absolute;
  right: 1.2rem;
  top: 50%;
  transform: translate3d(0, 0, 200px) translateY(-50%);
  border: 1px solid ${({ theme }) => theme?.grayScale?.[100]};
  border-radius: 50px;

  & > .custom-option_radioIcon_fill {
    width: 0.8rem;
    height: 0.8rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${({ theme }) => theme.brand.primary};
    border-radius: 50px;
    display: none; /* this will be enabled in main selectInput component */
  }

  ${mq.laptop} {
    display: ${({ isMobileDevice }) => !isMobileDevice && 'none'};
  }
`;

const StyledOption = styled.div<{
  hasSecondary?: boolean;
  isMobileDevice: boolean;
}>`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  position: relative;

  & .custom-option_secondary-label {
    margin-top: 0.8rem;
    display: ${({ hasSecondary }) => !hasSecondary && 'none'};
  }

  ${mq.laptop} {
    ${({ isMobileDevice }) =>
      !isMobileDevice &&
      css`
        justify-content: space-between;
        align-items: center;
        flex-direction: row;

        & .custom-option_secondary-label {
          margin-top: 0;
        }
      `}
  }
`;

export default SearchSelectCustomOption;
